import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtNum, fmtDate } from "@/lib/format";
import { Play, Calculator, TrendingUp, TrendingDown } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";

function StdCostTab() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [compute, setCompute] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [selItem, setSelItem] = useState("");

  const load = async () => {
    setRows((await api.get("/costing/standard")).data);
    setItems((await api.get("/items")).data);
  };
  useEffect(() => { load(); }, []);

  const runCompute = async () => {
    if (!selItem) return toast.error("Select an item");
    try { setCompute((await api.get(`/costing/standard/${selItem}`, { params: { save: false } })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const runSave = async () => {
    try { await api.get(`/costing/standard/${selItem}`, { params: { save: true } }); toast.success("Saved"); load(); setCompute(null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="std-cost-tab">
      <div className="card-sharp p-4 flex items-end gap-3">
        <div className="flex-1"><Label className="text-xs">Item</Label>
          <Select value={selItem} onValueChange={setSelItem}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="cost-item"><SelectValue placeholder="Select item to compute" /></SelectTrigger>
            <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
          </Select>
        </div>
        <Button onClick={runCompute} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="cost-compute"><Calculator className="w-4 h-4" /> Compute</Button>
        {compute && <Button onClick={runSave} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">Save snapshot</Button>}
      </div>

      {compute && (
        <div className="card-sharp p-4">
          <div className="grid grid-cols-5 gap-3">
            <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Material</div><div className="text-xl tabular-nums">{fmtINR(compute.material_cost)}</div></div>
            <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Machine</div><div className="text-xl tabular-nums">{fmtINR(compute.machine_cost)}</div></div>
            <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Labour</div><div className="text-xl tabular-nums">{fmtINR(compute.labour_cost)}</div></div>
            <div><div className="text-[10px] font-mono uppercase text-muted-foreground">Overhead</div><div className="text-xl tabular-nums">{fmtINR(compute.overhead_cost)}</div></div>
            <div><div className="text-[10px] font-mono uppercase text-accent">Total</div><div className="text-2xl font-medium tabular-nums text-accent">{fmtINR(compute.total_cost)}</div></div>
          </div>
          <div className="mt-3 pt-3 border-t flex items-center gap-4 text-sm">
            <span>Sale price: <span className="tabular-nums font-medium">{fmtINR(compute.sale_price)}</span></span>
            <span>Margin: <span className={`tabular-nums font-medium ${compute.margin_pct >= 20 ? "text-emerald-600" : compute.margin_pct >= 0 ? "text-yellow-500" : "text-red-500"}`}>{compute.margin_pct}%</span></span>
          </div>
        </div>
      )}

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Item</th><th>Effective from</th><th className="text-right">Material</th><th className="text-right">Machine</th><th className="text-right">Labour</th><th className="text-right">Overhead</th><th className="text-right">Total</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No saved standard costs yet.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => setDetailRow(r)} data-testid={`std-${r.item_sku}`}>
                <td>{r.item_name}<div className="text-[10px] font-mono text-muted-foreground">{r.item_sku}</div></td>
                <td className="text-xs">{fmtDate(r.effective_from)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(r.material_cost)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(r.machine_cost)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(r.labour_cost)}</td>
                <td className="text-right tabular-nums text-xs">{fmtINR(r.overhead_cost)}</td>
                <td className="text-right tabular-nums font-medium">{fmtINR(r.total_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Sheet open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl rounded-none space-y-4 overflow-y-auto" data-testid="std-cost-drawer">
          <SheetHeader className="pb-3 border-b">
            <SheetTitle className="font-heading flex items-center gap-3">
              <Calculator className="w-5 h-5 text-accent" />
              Standard Cost · {detailRow?.item_name}
              <span className="text-[10px] font-mono text-muted-foreground ml-auto">{detailRow?.item_sku}</span>
            </SheetTitle>
          </SheetHeader>
          {detailRow && (
            <div className="text-sm space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Material</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(detailRow.material_cost)}</div><div className="text-[10px] text-muted-foreground mt-0.5">{detailRow.total_cost > 0 ? ((detailRow.material_cost / detailRow.total_cost) * 100).toFixed(1) : 0}%</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Machine</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(detailRow.machine_cost)}</div><div className="text-[10px] text-muted-foreground mt-0.5">{detailRow.total_cost > 0 ? ((detailRow.machine_cost / detailRow.total_cost) * 100).toFixed(1) : 0}%</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Labour</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(detailRow.labour_cost)}</div><div className="text-[10px] text-muted-foreground mt-0.5">{detailRow.total_cost > 0 ? ((detailRow.labour_cost / detailRow.total_cost) * 100).toFixed(1) : 0}%</div></div>
                <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Overhead</div><div className="mt-1 text-lg font-bold tabular-nums">{fmtINR(detailRow.overhead_cost)}</div><div className="text-[10px] text-muted-foreground mt-0.5">{detailRow.total_cost > 0 ? ((detailRow.overhead_cost / detailRow.total_cost) * 100).toFixed(1) : 0}%</div></div>
              </div>
              <div className="card-sharp p-4 bg-accent/5">
                <div className="text-[10px] font-mono uppercase text-accent">// Standard cost per unit</div>
                <div className="mt-1 text-3xl font-bold tabular-nums">{fmtINR(detailRow.total_cost)}</div>
                <div className="text-xs text-muted-foreground mt-1">Effective from {fmtDate(detailRow.effective_from)} · BOM {detailRow.bom_code || detailRow.bom_id?.slice(0, 8) || "—"} · Routing {detailRow.routing_code || detailRow.routing_id?.slice(0, 8) || "—"}</div>
              </div>
              {(detailRow.material_breakdown || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Material breakdown ({detailRow.material_breakdown.length} components)</div>
                  <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs"><thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Component</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate ₹</th><th className="p-2 text-right">Cost ₹</th></tr></thead><tbody>
                    {detailRow.material_breakdown.map((c, i) => (<tr key={i} className="border-t"><td className="p-2">{c.item_name || c.name}<div className="text-[10px] font-mono text-muted-foreground">{c.item_sku || c.sku}</div></td><td className="p-2 text-right tabular-nums">{c.qty}</td><td className="p-2 text-right tabular-nums">{fmtINR(c.rate || c.unit_cost)}</td><td className="p-2 text-right tabular-nums font-medium">{fmtINR((c.qty || 0) * (c.rate || c.unit_cost || 0))}</td></tr>))}
                  </tbody></table></div>
                </div>
              )}
              {(detailRow.operation_breakdown || []).length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">// Operation breakdown ({detailRow.operation_breakdown.length} ops)</div>
                  <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs"><thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Op</th><th className="p-2 text-left">Machine</th><th className="p-2 text-right">Hrs</th><th className="p-2 text-right">MHR ₹</th><th className="p-2 text-right">Labour ₹</th></tr></thead><tbody>
                    {detailRow.operation_breakdown.map((o, i) => (<tr key={i} className="border-t"><td className="p-2">{o.operation_name || o.name}</td><td className="p-2 font-mono text-[10px]">{o.machine_code || "—"}</td><td className="p-2 text-right tabular-nums">{o.hours}</td><td className="p-2 text-right tabular-nums">{fmtINR(o.mhr_rate || 0)}</td><td className="p-2 text-right tabular-nums">{fmtINR(o.labour_cost || 0)}</td></tr>))}
                  </tbody></table></div>
                </div>
              )}
              <div className="border rounded-sm p-3 bg-muted/20 text-xs text-muted-foreground">
                <div className="font-mono text-[10px] uppercase mb-1">// How this is computed</div>
                Material = Σ (BOM component qty × current item cost). Machine = Σ (routing hrs × machine MHR rate). Labour = Σ (routing hrs × labour rate). Overhead = BOM overhead_cost_per_unit + machine overhead absorbed. All figures per 1 unit of output.
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function VarianceTab() {
  const [wos, setWos] = useState([]);
  const [wo, setWo] = useState("");
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/production/work-orders", { params: { status: "closed" } }).then((r) => setWos(r.data)); }, []);
  const run = async () => { if (!wo) return; try { setV((await api.get(`/costing/variance/${wo}`)).data); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };

  const Cell = ({ label, std, actual, variance }) => (
    <div className="card-sharp p-3">
      <div className="text-[10px] font-mono uppercase text-muted-foreground">{label}</div>
      <div className="text-sm tabular-nums">Std {fmtINR(std)}</div>
      <div className="text-sm tabular-nums">Act {fmtINR(actual)}</div>
      <div className={`text-lg font-medium tabular-nums ${variance > 0 ? "text-red-500" : "text-emerald-600"} flex items-center gap-1`}>
        {variance > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
        {fmtINR(Math.abs(variance))}
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="variance-tab">
      <div className="card-sharp p-4 flex items-end gap-3">
        <div className="flex-1"><Label className="text-xs">Closed Work Order</Label>
          <Select value={wo} onValueChange={setWo}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="variance-wo"><SelectValue placeholder="Select WO" /></SelectTrigger>
            <SelectContent>{wos.map((w) => <SelectItem key={w.id} value={w.id}>{w.wo_number} · {w.item_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="variance-run">Analyse</Button>
      </div>
      {v && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Cell label="Material" std={v.std_material} actual={v.actual_material} variance={v.variance_material} />
            <Cell label="Machine"  std={v.std_machine}  actual={v.actual_machine}  variance={v.variance_machine} />
            <Cell label="Labour"   std={v.std_labour}   actual={v.actual_labour}   variance={v.variance_labour} />
            <div className={`card-sharp p-3 ${v.variance_total > 0 ? "border-red-500/40" : "border-emerald-500/40"}`}>
              <div className="text-[10px] font-mono uppercase text-accent">TOTAL VARIANCE</div>
              <div className="text-sm tabular-nums">Std {fmtINR(v.std_total)}</div>
              <div className="text-sm tabular-nums">Act {fmtINR(v.actual_total)}</div>
              <div className={`text-xl font-medium tabular-nums ${v.variance_total > 0 ? "text-red-500" : "text-emerald-600"}`}>{fmtINR(v.variance_total)} ({v.variance_pct}%)</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Costing() {
  const { hasModule } = useSubscription();
  if (!hasModule("costing")) return <div className="p-8"><div className="card-sharp p-10 text-center"><div className="font-heading text-2xl">Costing requires Enterprise Manufacturing</div><a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a></div></div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="costing-page">
      <PageHeader eyebrow="Manufacturing" title="Costing · Standard vs Actual"
        description="Compute standard cost from BOM + Routing, snapshot it, and reconcile against actuals on closed Work Orders. Material, Machine, Labour and Overhead variances broken out." />
      <Tabs defaultValue="std">
        <TabsList className="rounded-sm">
          <TabsTrigger value="std" className="rounded-sm" data-testid="tab-std">Standard Cost</TabsTrigger>
          <TabsTrigger value="variance" className="rounded-sm" data-testid="tab-variance">Variance</TabsTrigger>
        </TabsList>
        <TabsContent value="std" className="mt-4"><StdCostTab /></TabsContent>
        <TabsContent value="variance" className="mt-4"><VarianceTab /></TabsContent>
      </Tabs>
    </div>
  );
}
